/**
 * PolicyTermField — the control AC-2 and AC-3 depend on.
 *
 * The grouped form must contain a control that WRITES the chosen term, not only
 * one that displays it. Without it nothing sets `policy_term_key`, so
 * `validateEffectiveDateAgainstPolicy` has no chosen term to validate against
 * (AC-3 unreachable), AC-2's payload assertion cannot be driven by "select the
 * renewal term", and Phase 1's move of `policyPrefillOptions.ts` would have
 * relocated a selection helper nothing re-consumes.
 *
 * **The term-identity write is the whole of AC-2 and it is NEW work.** Today the
 * term DATES are not form state at all: the drawer derives them from `chosenTerm`
 * and injects them into RUN-LEVEL inputs. Under the reshaped contract the quartet
 * is PER ACTION, so nothing run-level can supply it — this control is the only
 * writer, and the drawer's run-level injection is superseded in the reshaped
 * branch (Phase 5 task 2 records the hand-over).
 *
 * A declared term-identity key with no `TERM_IDENTITY_SOURCES` entry, or a
 * MANDATORY key (policy number, the two term dates) whose source is blank on the
 * selected row, is a LOUD failure in the PAD-2 sense —
 * `missingTermIdentitySources` — surfaced by `LaunchForm` exactly like
 * `missingRoles` and blocking submit. Writing a partial identity silently is the
 * original defect (a bare policy number letting a future-term change land on the
 * current term, instances 3b35db7f / e036c04d, 2026-08-05); an empty string for a
 * declared term key is that same defect wearing a key.
 *
 * The one exception is `policy_type_code` (`OPTIONAL_TERM_IDENTITY_KEYS`, story
 * 28631): Epic's line code does not say WHICH term this is, so a row without one
 * is not a partial identity. It is written as `null` and the workflow falls back
 * to the human gate.
 */

import type { ReactNode } from "react";
import type { FieldSchema } from "~/common/workflows/schemaHelpers";
import type { PolicyInForce } from "~/common/workflows/types/client";
// The named export, not the default: the module exports BOTH under this name,
// and importing the default under it trips `import/no-named-as-default`.
import { PolicyNumberCombobox } from "~/common/workflows/launch/PolicyNumberCombobox";
import {
  POLICY_TERM_KEY_FIELD,
  policyTermKey,
  readPolicyTermKey,
} from "~/common/workflows/launch/policyPrefillOptions";
import {
  interestAutoPickPatch,
  interestResetPatch,
} from "~/common/workflows/launch/interestTargetOptions";
import {
  LAUNCH_ROLE_CANDIDATES,
  type LaunchBinding,
} from "~/common/workflows/launch/launchSchemaBinding";

/**
 * How each DECLARED term-identity key is resolved from the selected option row.
 *
 * One documented map, in one module — the alternative is four resolution rules
 * scattered across handlers, which is how three of the four quietly stop being
 * written.
 *
 * `policy_type_code` is Epic's OWN line code ("PHOM"), read from
 * `ams_policy_type_code` — RESOLVED (was assumption Q8, story 28631). The
 * workflow maps `actions[0].policy_type_code` to `ams_policy_type_code` and its
 * deterministic matcher compares it code-to-code against the line code Epic
 * shows, so the product group ("Homeowners") can never match and must never be
 * a fallback here: it made confident auto-skip dead and asked the AM to confirm
 * twice even at oversight NONE (TEST instances 3fc6e693 / ac7f759a). A row that
 * carries no code resolves to blank, which is "no code" and fails safe to the
 * human — see `OPTIONAL_TERM_IDENTITY_KEYS`.
 */
export const TERM_IDENTITY_SOURCES: Record<
  string,
  (opt: PolicyInForce) => unknown
> = {
  policy_number: (o) => o.policy_number,
  policy_type_code: (o) => o.ams_policy_type_code,
  term_effective_date: (o) => o.effective_date,
  term_expiration_date: (o) => o.expiration_date,
};

/**
 * Term-identity keys whose blank source on the selected row is NOT a defect.
 *
 * Epic's line code is an optimisation for the matcher, not part of WHICH term
 * this is (policy number + the two term dates say that). A row without one — a
 * source that does not carry it — must still launch, with `null` written so no
 * earlier pick's code lingers; the workflow reads null as "no code" and the human
 * gate runs. The three keys that DO identify the term stay mandatory, so the
 * 2026-08-05 wrong-term defect stays loud. A key with no `TERM_IDENTITY_SOURCES`
 * entry is still reported even when optional: that is the contract declaring
 * something this map does not know, not a blank value.
 */
export const OPTIONAL_TERM_IDENTITY_KEYS: ReadonlySet<string> = new Set([
  "policy_type_code",
]);

/** Run-level hidden key the workflow reads as the Epic client match key. */
export const CLIENT_LOOKUP_CODE_KEY = "clientlookup_code";

function isBlank(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    (typeof value === "string" && value.trim() === "")
  );
}

export interface TermIdentityPlacement {
  /**
   * Where the declared term identity lives — and therefore where the ONE term
   * control renders. `null` means the schema declares no term-identity key at
   * all, which `buildLaunchBinding` has already reported as a missing role.
   */
  level: "action" | "runLevel" | null;
  /** The declared term-identity keys at that level, in candidate order. */
  keys: string[];
}

/**
 * Resolve where the term control renders — from the DECLARED schema, never from
 * a hardcoded assumption about where the contract puts the quartet.
 *
 * Action level wins when the contract declares the identity there (the reshaped
 * contract does). The run-level fallback exists so a contract that keeps the
 * quartet on the envelope still gets a writer rather than a read-only header,
 * which would make AC-2 and AC-3 unreachable.
 */
export function resolveTermIdentityPlacement(
  binding: LaunchBinding,
): TermIdentityPlacement {
  if (binding.roles.termIdentity.length > 0) {
    return { level: "action", keys: binding.roles.termIdentity };
  }
  const runLevel = LAUNCH_ROLE_CANDIDATES.termIdentity.filter((k) =>
    binding.declaredKeys.runLevel.has(k),
  );
  return runLevel.length > 0
    ? { level: "runLevel", keys: runLevel }
    : { level: null, keys: [] };
}

/**
 * Declared term-identity keys this control cannot honestly write.
 *
 * Two cases, both loud: a key with no `TERM_IDENTITY_SOURCES` entry (the
 * contract declared something this map does not know — reported even for an
 * optional key), and — once a row is selected — a MANDATORY key whose source is
 * blank on that row. A blank `OPTIONAL_TERM_IDENTITY_KEYS` key (`policy_type_code`)
 * is deliberately NOT reported and does not block submit (story 28631 AC4).
 */
export function missingTermIdentitySources(
  keys: readonly string[],
  option: PolicyInForce | null,
): string[] {
  const missing: string[] = [];
  for (const key of keys) {
    const source = TERM_IDENTITY_SOURCES[key];
    if (!source) {
      missing.push(key);
      continue;
    }
    if (
      option &&
      !OPTIONAL_TERM_IDENTITY_KEYS.has(key) &&
      isBlank(source(option))
    ) {
      missing.push(key);
    }
  }
  return missing;
}

/**
 * The FULL declared term identity, resolved from the selected row.
 *
 * A blank OPTIONAL key is written as `null` — not "" and never a stand-in such
 * as the product group — so the merge overwrites whatever an earlier pick left
 * there and the payload says "no code" explicitly (story 28631 AC4).
 */
export function termIdentityWrites(
  keys: readonly string[],
  option: PolicyInForce,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const key of keys) {
    const source = TERM_IDENTITY_SOURCES[key];
    if (!source) continue;
    const value = source(option);
    patch[key] =
      OPTIONAL_TERM_IDENTITY_KEYS.has(key) && isBlank(value) ? null : value;
  }
  return patch;
}

/**
 * Every declared term-identity key back to `""` — not the policy number alone.
 * A cleared policy with a stale `term_effective_date` still on the action is a
 * term identity nobody chose.
 */
export function termIdentityClears(
  keys: readonly string[],
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const key of keys) patch[key] = "";
  return patch;
}

export interface PolicyTermFieldArgs {
  binding: LaunchBinding;
  /**
   * `mergeLaunchSlice(binding, runLevelValues, slice)` — reads go through the
   * merge because `interestResetPatch` / `interestAutoPickPatch` /
   * `readInterestOptionsForSelection` all read `policy_options`,
   * `interest_options_by_line`, `policy_number` and `policy_term_key` off ONE
   * record, and after normalisation those halves live at different levels.
   * Passing the slice alone leaves the prefill echoes behind: auto-pick and
   * `OldMortgageeSelect` then see no options.
   */
  helperValues: Record<string, unknown>;
  policyOptions: readonly PolicyInForce[];
  /** Mixed-patch writer — `LaunchForm` routes each key to its declared slice. */
  writePatch: (patch: Record<string, unknown>) => void;
  submitting?: boolean;
}

/**
 * Build the term control's `onSelect` patch.
 *
 * Order matters and is carried over verbatim from today's `policy_number`
 * override:
 *  1. `interestResetPatch` — a target resolved against the PREVIOUS term cannot
 *     describe this one; carrying it across submits another term's mortgagee row
 *     with no error anywhere.
 *  2. the FULL declared term identity from the selected row — not the policy
 *     number alone.
 *  3. `policy_term_key` — WHICH term was picked. The number alone cannot say,
 *     when a current term and its future renewal share it.
 *  4. the term-DERIVED fields (`policy_type` / `carrier_name` / `carrier_id`),
 *     each written only when the row carries it. They render locked.
 *     Run-level `clientlookup_code` is the exception to "only when present": when
 *     `writesClientLookup` (the schema declares it at run level) it is ALWAYS
 *     written, `null` when the row has none, so a code from an earlier pick (or
 *     another client's row) can never ride along with this term (story 28631).
 *     `LaunchForm.writePatch` routes it to run level because the action does not
 *     declare it. When the schema does not declare it nothing is written, so
 *     `buildLaunchPayload` has nothing to report as dropped.
 *  5. `interestAutoPickPatch` against the switched-TO term, so a term with
 *     exactly one interest pre-selects it — the same rule the server applies to
 *     the term IT resolved.
 */
export function buildTermSelectPatch(
  keys: readonly string[],
  helperValues: Record<string, unknown>,
  option: PolicyInForce,
  writesClientLookup: boolean,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {
    ...interestResetPatch(helperValues),
    ...termIdentityWrites(keys, option),
    [POLICY_TERM_KEY_FIELD]: policyTermKey(option),
    // Gated on the schema DECLARING the key — the same gate as
    // `assembleLaunchInputs.withClientLookupCode` — so an older contract does not
    // have it reported in `dropped` (and logged) on every launch.
    ...(writesClientLookup
      ? { [CLIENT_LOOKUP_CODE_KEY]: option.clientlookup_code ?? null }
      : {}),
  };
  const group = option.product_group ?? option.policy_type;
  if (group) patch.policy_type = group;
  if (option.carrier_name) patch.carrier_name = option.carrier_name;
  if (option.carrier_id) patch.carrier_id = option.carrier_id;
  // Re-merged, never the pre-switch values and never the slice alone.
  return { ...patch, ...interestAutoPickPatch({ ...helperValues, ...patch }) };
}

/** The `onClear` patch — no policy means no term, so no per-term target either. */
export function buildTermClearPatch(
  keys: readonly string[],
  helperValues: Record<string, unknown>,
  writesClientLookup: boolean,
): Record<string, unknown> {
  return {
    ...interestResetPatch(helperValues),
    ...termIdentityClears(keys),
    [POLICY_TERM_KEY_FIELD]: "",
    ...(writesClientLookup ? { [CLIENT_LOOKUP_CODE_KEY]: null } : {}),
  };
}

export interface PolicyTermFieldProps extends PolicyTermFieldArgs {
  /** The declared policy-number field, for label / required. */
  field: FieldSchema;
  readOnly?: boolean;
  /** Narrow-column layout (the pane): dates and line under the number. */
  stacked?: boolean;
}

export default function PolicyTermField({
  binding,
  helperValues,
  policyOptions,
  writePatch,
  submitting = false,
  field,
  readOnly = false,
  stacked = false,
}: PolicyTermFieldProps) {
  const { keys } = resolveTermIdentityPlacement(binding);
  const writesClientLookup = binding.declaredKeys.runLevel.has(
    CLIENT_LOOKUP_CODE_KEY,
  );
  const valueKey = keys[0] ?? "policy_number";
  const value =
    typeof helperValues[valueKey] === "string"
      ? (helperValues[valueKey] as string)
      : "";

  return (
    <PolicyNumberCombobox
      density="compact"
      layout={stacked ? "stacked" : "inline"}
      label={field.label}
      required={field.required}
      value={value}
      termKey={readPolicyTermKey(helperValues)}
      options={policyOptions}
      disabled={readOnly || submitting}
      // The switch performs NO server re-trigger: the selected row carries
      // everything the pick drives, and re-running extraction would clobber the
      // AM's manual edits.
      onSelect={(opt) =>
        writePatch(
          buildTermSelectPatch(keys, helperValues, opt, writesClientLookup),
        )
      }
      onClear={() =>
        writePatch(buildTermClearPatch(keys, helperValues, writesClientLookup))
      }
    />
  );
}

/**
 * The same control as a `JsonSchemaForm` `fieldOverride`, for the case where the
 * declared term identity lives on the ACTION and the control therefore renders
 * inside the action card.
 */
export function policyTermFieldOverride(
  args: PolicyTermFieldArgs,
): (renderArgs: {
  field: FieldSchema;
  value: unknown;
  setValue: (key: string, next: unknown) => void;
  readOnly: boolean;
}) => ReactNode {
  // A `JsonSchemaForm` field-override is a RENDER CALLBACK, not a component —
  // it has no lifecycle of its own and the renderer never mounts it as one — so
  // the two component-shaped rules do not apply. TypeScript types the argument.
  // eslint-disable-next-line react/display-name, react/prop-types
  return ({ field, readOnly }) => (
    <PolicyTermField {...args} field={field} readOnly={readOnly} />
  );
}
